import React, { useEffect } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import Navigation from '../Navigation/Navigation';
import SignUp from '../../pages/SignUp/SignUp';
import SignIn from '../../pages/SignIn/SignIn';
import PasswordForget from '../../pages/PasswordForget/PasswordForget';
import AddAudio from '../../pages/AddAudio/AddAudio';
import EditAudio from '../../pages/EditAudio/';
import Account from '../../pages/Account/Account';
import Invite from '../../pages/Invite';
import ClaimLicense from '../../pages/ClaimLicense';
import SelectOrganization from '../../pages/SelectOrganization';
import GetStarted from '../../pages/GetStarted';
import Admin from '../../pages/Admin/Admin.jsx';
import ModalProvider from '../utility/Modal/ModalProvider';
import ModalRoot from '../utility/Modal/ModalRoot';
import GlobalPlayerProvider from '../utility/GlobalPlayer/GlobalPlayerProvider';
import GlobalPlayerRoot from '../utility/GlobalPlayer/GlobalPlayerRoot';
import LoaderOverlayProvider from '../LoaderOverlay/LoaderOverlayContext';
import LoaderOverlayRoot from '../LoaderOverlay/LoaderOverlay';
import * as ROUTES from '../../constants/routes';
import { withAuthentication } from '../Session';
import './app.scss';
import TracksListener from '../../listeners/TrackListener';
import PublicTracksListener from '../../listeners/PublicTracksListener';
import Firebase, { FirebaseContext } from '../Firebase';
import { useCollectionCache } from '../../services/collectionCache.service';
import { useAuthStore } from '../../store/auth.store';
import { useOrganizationStore } from '../../store/organization.store';
import { useChatbotStore } from '../../store/chatbot.store';
import FindAudioWithPlaylistFilter from '../../pages/FindAudio/FindAudioWithPlaylistFilter';
import PublicFindAudio from '../../pages/FindAudio/PublicFindAudio';
import { PlaylistProvider } from '../../components/PlaylistContext';
import { ErrorBoundary, RouteErrorBoundary, ComponentErrorBoundary } from '../ErrorBoundary';

const firebase = new Firebase();

const App = () => {
  const loadEssentialCollections = useCollectionCache(state => state.loadEssentialCollections);
  const loadCollectionData = useCollectionCache(state => state.loadCollectionData);
  const clearCache = useCollectionCache(state => state.clearCache);
  const fetchChatbotConfig = useChatbotStore(state => state.fetchConfig);
  const setAuth = useAuthStore(state => state.setAuth);
  const setRole = useAuthStore(state => state.setRole);
  const setDisplayPreferences = useAuthStore(state => state.setDisplayPreferences);

  // Organization store actions
  const setOrganizations = useOrganizationStore(state => state.setOrganizations);
  const setCurrentOrganization = useOrganizationStore(state => state.setCurrentOrganization);
  const setSuperAdmin = useOrganizationStore(state => state.setSuperAdmin);
  const setOrgLoading = useOrganizationStore(state => state.setLoading);
  const clearOrganization = useOrganizationStore(state => state.clearOrganization);

  // Initialize collection cache and handle auth state changes
  useEffect(() => {
    const unsubscribe = firebase.onAuthUserListener(
      async (authUser: any) => {
        if (authUser) {
          // IMPORTANT: Set org loading FIRST before setting auth
          // This prevents the SignIn page from redirecting before orgs are loaded
          setOrgLoading(true);

          // Set auth in store
          setAuth(authUser);

          // Load essential collections (genres, tags) for chatbot functionality
          // Must be done after auth since Firestore rules require authentication
          loadEssentialCollections(firebase);
          // Load Gemini API key for AI features (chatbot)
          fetchChatbotConfig(firebase);

          // Set role based on explicit role checks
          const isAdmin = authUser.roles && authUser.roles['ADMIN'] === 'ADMIN';
          const isOperator = authUser.roles && authUser.roles['ADMIN'] === 'OPERATOR';

          // Set role and load collections based on role
          if (isAdmin) {
            setRole('ADMIN');
          } else if (isOperator) {
            setRole('OPERATOR');
          } else {
            setRole('USER');
          }

          // Handle organization membership
          try {
            // Check for super admin status
            const isSuperAdmin = authUser.isSuperAdmin === true;
            setSuperAdmin(isSuperAdmin);

            // Check for organizations pending restoration
            // (orgs where user is in members subcollection but org has restoreRequested flag)
            await firebase.checkAndRestorePendingOrgs(authUser.uid);

            // Get user's organizations from their profile (refresh after potential restore)
            const organizations = await firebase.getUserOrganizations(authUser.uid);
            setOrganizations(organizations);

            // If user has organizations and currentOrgId is persisted, verify it's valid
            // Read directly from store to get the current value (not stale closure)
            let selectedOrgId: string | null = null;
            if (organizations.length > 0) {
              const persistedOrgId = useOrganizationStore.getState().currentOrgId;
              const validOrg = organizations.find((o: any) => o.orgId === persistedOrgId);

              if (validOrg) {
                // Use the persisted org
                setCurrentOrganization(validOrg.orgId, validOrg.orgName, validOrg.role, validOrg.orgType || 'artist', validOrg.orgCategory);
                selectedOrgId = validOrg.orgId;
              } else if (organizations.length === 1) {
                // Only auto-select if user has exactly one org
                const firstOrg = organizations[0];
                setCurrentOrganization(firstOrg.orgId, firstOrg.orgName, firstOrg.role, firstOrg.orgType || 'artist', firstOrg.orgCategory);
                selectedOrgId = firstOrg.orgId;
              } else {
                // Persisted org is invalid for this user and they have multiple orgs
                // Clear the invalid persisted orgId so they go to SelectOrganization
                clearOrganization();
                setOrganizations(organizations); // Re-set orgs after clear
                setSuperAdmin(isSuperAdmin); // Re-set super admin status after clear
              }
            }

            // Load org-scoped collections only if an org is selected
            // Global collections (genres, tags, keys, bpm) are loaded via loadEssentialCollections above
            if (selectedOrgId) {
              loadCollectionData(firebase, selectedOrgId);
              // Load user's display preferences for this org
              firebase.getMemberDisplayPreferences(selectedOrgId, authUser.uid).then((prefs: any) => {
                setDisplayPreferences(prefs);
              });
            }
          } catch (error) {
            console.error('Error loading organization data:', error);
          } finally {
            setOrgLoading(false);
          }
        } else {
          // Clear auth and role
          setAuth(null);
          setRole('USER');

          // Clear organization state
          clearOrganization();

          // Set loading to false since there's no user to load orgs for
          setOrgLoading(false);

          // Clear collection cache
          clearCache();
        }
      },
      () => {
        // Handle auth error - also need to stop loading
        setAuth(null);
        setRole('USER');
        clearOrganization();
        setOrgLoading(false);
        clearCache();
      }
    );

    return () => {
      unsubscribe();
      clearCache();
    };
  // Note: currentOrgId is intentionally NOT in deps - we read it via getState() inside the callback
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadEssentialCollections, loadCollectionData, clearCache, setAuth, setRole, setOrganizations, setCurrentOrganization, setSuperAdmin, setOrgLoading, clearOrganization]);

  return (
    <ErrorBoundary>
      <FirebaseContext.Provider value={firebase}>
        <BrowserRouter>
          <ComponentErrorBoundary name="TracksListener" minimal>
            <TracksListener />
          </ComponentErrorBoundary>
          <ComponentErrorBoundary name="PublicTracksListener" minimal>
            <PublicTracksListener />
          </ComponentErrorBoundary>
          <div>
            <LoaderOverlayProvider>
              <GlobalPlayerProvider>
                <ModalProvider>
                  <ComponentErrorBoundary name="ModalRoot" minimal>
                    <ModalRoot />
                  </ComponentErrorBoundary>

                  <ComponentErrorBoundary name="Navigation">
                    <Navigation />
                  </ComponentErrorBoundary>

                  <PlaylistProvider mode="library">
                    <RouteErrorBoundary>
                      <Switch>
                        <Route
                          path={ROUTES.PASSWORD_FORGET}
                          component={PasswordForget}
                        />
                        <Route path={ROUTES.FIND_AUDIO} component={FindAudioWithPlaylistFilter} />
                        <Route path={ROUTES.PUBLIC_CATALOGUE} component={PublicFindAudio} />
                        <Route path={ROUTES.ADD_AUDIO} component={AddAudio} />
                        <Route
                          path={`${ROUTES.EDIT_AUDIO}/:trackId`}
                          component={EditAudio}
                        />
                        <Route path={ROUTES.ACCOUNT} component={Account} />
                        <Route path={ROUTES.ADMIN} component={Admin} />
                        <Route path={ROUTES.SIGN_UP} component={SignUp} />
                        <Route path={ROUTES.INVITE} component={Invite} />
                        <Route path={ROUTES.CLAIM_LICENSE} component={ClaimLicense} />
                        <Route path={ROUTES.SELECT_ORGANIZATION} component={SelectOrganization} />
                        <Route path={ROUTES.GET_STARTED} component={GetStarted} />
                        <Route exact path={ROUTES.SIGN_IN} component={SignIn} />
                      </Switch>
                    </RouteErrorBoundary>
                  </PlaylistProvider>

                  <ComponentErrorBoundary name="GlobalPlayer" minimal>
                    <GlobalPlayerRoot />
                  </ComponentErrorBoundary>

                  <ComponentErrorBoundary name="LoaderOverlay" minimal>
                    <LoaderOverlayRoot />
                  </ComponentErrorBoundary>
                </ModalProvider>
              </GlobalPlayerProvider>
            </LoaderOverlayProvider>
          </div>
        </BrowserRouter>
      </FirebaseContext.Provider>
    </ErrorBoundary>
  );
};

export default withAuthentication(App);
